import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, RotateCw, Sparkles } from "lucide-react";
import { api } from "../../../lib/api";
import ArticleSearchBar from "../../../components/shared/ArticleSearchBar";
import { articleMatchesSearch } from "../../../lib/articleSearch";

function SeriesArticleCard({ post }) {
  return (
    <Link
      to={`/workspace/pro-carer-circle/advantage-series/${post.slug}`}
      className="card-base overflow-hidden flex flex-col group min-w-0"
      data-testid={`advantage-series-card-${post.slug}`}
    >
      {post.featured_image && (
        <div className="aspect-[16/9] overflow-hidden">
          <img src={post.featured_image} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-6 flex flex-col flex-1 min-w-0">
        <div className="flex items-center justify-between gap-3 mb-3">
          <span className="pill bg-amber-50 text-amber-800">{post.category}</span>
          {post.read_minutes ? (
            <span className="text-xs text-ink-soft flex items-center gap-1 shrink-0">
              <Clock className="w-3.5 h-3.5" /> {post.read_minutes} min read
            </span>
          ) : null}
        </div>
        <h3 className="font-display text-xl font-semibold leading-snug group-hover:text-teal transition-colors">
          {post.title}
        </h3>
        <p className="mt-2 text-ink-muted text-sm flex-1">{post.excerpt}</p>
        <div className="mt-4 flex items-center gap-1 text-teal font-medium text-sm">
          Read more <ArrowRight className="w-4 h-4" />
        </div>
      </div>
    </Link>
  );
}

export default function AdvantageSeries() {
  const [posts, setPosts] = useState([]);
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [nonce, setNonce] = useState(0);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("All");

  const categories = useMemo(() => {
    const set = new Set(posts.map((p) => p.category).filter(Boolean));
    return ["All", ...Array.from(set)];
  }, [posts]);

  const filtered = posts.filter(
    (p) => (cat === "All" || p.category === cat) && articleMatchesSearch(p, search, ["title", "excerpt", "category"])
  );
  const clearSearchAndFilters = () => {
    setSearch("");
    setCat("All");
  };

  const fetchPosts = useCallback((signal) => {
    setStatus("loading");
    return api
      .get("/api/workspace/pro-carer-circle/advantage-series", { signal })
      .then((r) => {
        setPosts(r.data.posts || []);
        setStatus("ok");
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        setStatus("error");
      });
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    fetchPosts(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchPosts, nonce]);

  return (
    <div className="min-h-screen bg-surface-carer" data-testid="page-advantage-series">
      <div className="container-jcc py-8 md:py-14 max-w-5xl">
        <Link
          to="/workspace/pro-carer-circle"
          className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink"
          data-testid="advantage-series-back-to-hub"
        >
          <ArrowLeft className="w-4 h-4" /> Pro Carer Office
        </Link>

        <header className="mt-6 max-w-2xl">
          <div className="overline text-amber-700 flex items-center gap-2">
            <Sparkles className="w-4 h-4" strokeWidth={2} /> Pro Carer Office · Members Only
          </div>
          <h1 className="mt-2 font-display font-bold text-3xl md:text-4xl break-words">
            The Professional Carer Advantage Series
          </h1>
          <p className="mt-3 text-ink-muted leading-relaxed">
            Articles to help you grow a confident, sustainable independent care practice — written for Pro Carer Office members.
          </p>
        </header>

        <div className="mt-10 min-w-0">
          {status === "loading" && (
            <div className="text-center py-16 text-ink-muted" data-testid="advantage-series-loading">Loading articles…</div>
          )}

          {status === "error" && (
            <div className="text-center py-16" data-testid="advantage-series-error">
              <p className="text-ink-muted mb-4">Something went wrong loading these articles.</p>
              <button
                type="button"
                onClick={() => setNonce((n) => n + 1)}
                className="btn-outline inline-flex items-center gap-2"
                data-testid="advantage-series-retry"
              >
                <RotateCw className="w-4 h-4" /> Try again
              </button>
            </div>
          )}

          {status === "ok" && posts.length === 0 && (
            <div className="text-center py-16 rounded-2xl border border-slate-200 bg-white" data-testid="advantage-series-empty">
              <p className="text-ink-muted">No articles yet — check back soon.</p>
            </div>
          )}

          {status === "ok" && posts.length > 0 && (
            <>
              <ArticleSearchBar value={search} onChange={setSearch} testId="advantage-series-search" />
              <div className="flex flex-wrap gap-2 mb-8" data-testid="advantage-series-categories">
                {categories.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setCat(c)}
                    data-testid={`advantage-series-cat-${c.toLowerCase().replace(/[ &]+/g, "-")}`}
                    className={`pill px-3 py-1.5 ${
                      cat === c ? "bg-ink text-white" : "bg-slate-100 text-ink-muted hover:bg-slate-200"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>

              {filtered.length === 0 ? (
                <div className="text-center py-16 rounded-2xl border border-slate-200 bg-white" data-testid="advantage-series-search-empty">
                  <p className="font-display text-lg font-semibold mb-2">No articles found</p>
                  <p className="text-ink-muted text-sm mb-5">Try a different search or topic.</p>
                  <button
                    type="button"
                    onClick={clearSearchAndFilters}
                    className="btn-outline inline-flex"
                    data-testid="advantage-series-clear-search"
                  >
                    Clear search and filters
                  </button>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="advantage-series-grid">
                  {filtered.map((p) => <SeriesArticleCard key={p.slug} post={p} />)}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
